import { Maybe } from './taxonomyTotalItems';

type Option = {
  increment: number | null;
  maxValue: number | null;
};

type QuantityOptions = {
  limit: Map<number, Option>;
};

type Lookup = {
  key: number;
  increment: number | null;
};

function getInitialKeys(entries: [number, Option][]) {
  const [first, second] = entries;

  return [first[0], second ? second[0] : null];
}

function findCurrentAndNextKeys(
  entries: [number, Option][],
  currentQuantity: number,
) {
  let currentKey: number | null = null;
  let nextKey: number | null = null;

  for (const [key] of entries) {
    if (key > currentQuantity) {
      nextKey = key;
      break;
    } else {
      currentKey = key;
    }
  }

  return [currentKey, nextKey];
}

function convertStringToLookupObject(value: string): Lookup {
  const [keyStr, incrementStr] = value.split('x');
  const key = parseInt(keyStr);
  const increment = incrementStr ? parseInt(incrementStr) : null;

  return {
    key,
    increment,
  };
}

export function getCurrentOptionKeys(
  quantityOptions: QuantityOptions,
  currentQuantity?: number | null,
): { nextKey: number | null; currentKey: number | null } {
  const entries = Array.from(quantityOptions.limit.entries());
  let currentKey: number | null = null;
  let nextKey: number | null = null;

  if (!currentQuantity) {
    [currentKey, nextKey] = getInitialKeys(entries);
  } else {
    [currentKey, nextKey] = findCurrentAndNextKeys(entries, currentQuantity);
  }

  return { currentKey, nextKey };
}

export function createLookup(input: string[]): QuantityOptions {
  if (!input) {
    return {
      limit: new Map(),
    };
  }
  const result: QuantityOptions = {
    limit: new Map(),
  };

  input.forEach((value, i) => {
    const lookup = convertStringToLookupObject(value);
    const nextLookup: Lookup | null | undefined = input[i + 1]
      ? convertStringToLookupObject(input[i + 1])
      : input[i + 1] === null
      ? null
      : undefined;

    result.limit.set(lookup.key, {
      increment: lookup.increment,
      maxValue: nextLookup?.key ?? null,
    });
  });

  return result;
}

export function nextQuantity(
  quantityOptionsInput: string[],
  currentQuantity?: number | null,
  type: 'increment' | 'decrement' = 'increment',
): number | null {
  // if (currentQuantity === null) {
  // 	return null;
  // }

  const quantityOptions = createLookup(quantityOptionsInput);
  const { currentKey, nextKey } = getCurrentOptionKeys(
    quantityOptions,
    currentQuantity,
  );

  if (type === 'decrement') {
    if (currentKey === null) return null;
    return currentKey;
  }

  if (!currentQuantity) {
    return currentKey;
  }

  const currentQuantityOption = currentKey
    ? quantityOptions.limit.get(currentKey)
    : undefined;

  if (!currentQuantityOption?.increment) {
    return nextKey;
  }

  const newValue = currentQuantity + currentQuantityOption.increment;

  if (nextKey && newValue >= nextKey) return nextKey;

  return newValue;
}

export const shortenQuantity = (quantity: number, unit?: Maybe<string>) => {
  if (quantity > 999 && unit?.toLocaleLowerCase() === 'kg')
    return `${quantity / 1000} T`;
  return `${quantity} ${unit}`;
};
